import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LayoutTemplate, Plus, Trash2, Pencil, Loader2, Copy, ChevronUp, ChevronDown,
  Tv, Film, Clapperboard, Crown, User, X, GripVertical, DownloadCloud,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPE_ICON = { live: Tv, movie: Film, series: Clapperboard };
const TYPE_LABEL = { live: "Live TV", movie: "Movies", series: "Series" };

function uid() { return Math.random().toString(36).slice(2, 10); }

function CategoryLoader({ links, onLoaded }) {
  const [mode, setMode] = useState("m3u");
  const [url, setUrl] = useState("");
  const [xt, setXt] = useState({ server_url: "", username: "", password: "" });
  const [linkId, setLinkId] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      let payload;
      if (mode === "pool") payload = { link_id: linkId };
      else if (mode === "xtream") payload = { link_type: "xtream", ...xt };
      else payload = { link_type: "m3u", url };
      const { data } = await api.post("/templates/parse-sample", payload);
      onLoaded(data.categories);
      const b = data.breakdown || {};
      toast.success(`Loaded ${data.entry_count} channels · ${data.categories.length} categories (${b.live || 0} live, ${b.movie || 0} movies, ${b.series || 0} series)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const canLoad = mode === "pool" ? !!linkId : mode === "xtream" ? (xt.server_url && xt.username && xt.password) : !!url;

  return (
    <div className="rounded-md border border-border bg-secondary/30 p-3 space-y-3" data-testid="category-loader">
      <p className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground flex items-center gap-1.5"><DownloadCloud className="h-3.5 w-3.5" /> Load categories from a link</p>
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="w-full">
          <TabsTrigger value="m3u" className="flex-1" data-testid="loader-tab-m3u">M3U</TabsTrigger>
          <TabsTrigger value="xtream" className="flex-1" data-testid="loader-tab-xtream">Xtream</TabsTrigger>
          <TabsTrigger value="pool" className="flex-1" data-testid="loader-tab-pool">From Pool</TabsTrigger>
        </TabsList>
      </Tabs>
      {mode === "m3u" && (
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://provider.com/get.php?..." className="font-mono text-xs" data-testid="loader-url" />
      )}
      {mode === "xtream" && (
        <div className="space-y-2">
          <Input value={xt.server_url} onChange={(e) => setXt((s) => ({ ...s, server_url: e.target.value }))} placeholder="http://host:8080" className="font-mono text-xs" data-testid="loader-server" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={xt.username} onChange={(e) => setXt((s) => ({ ...s, username: e.target.value }))} placeholder="username" className="font-mono text-xs" data-testid="loader-username" />
            <Input value={xt.password} onChange={(e) => setXt((s) => ({ ...s, password: e.target.value }))} placeholder="password" className="font-mono text-xs" data-testid="loader-password" />
          </div>
        </div>
      )}
      {mode === "pool" && (
        <Select value={linkId} onValueChange={setLinkId}>
          <SelectTrigger data-testid="loader-pool-select"><SelectValue placeholder="Select a Link Pool entry" /></SelectTrigger>
          <SelectContent className="bg-popover">
            {links.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No links in pool yet</div> :
              links.map((l) => <SelectItem key={l.id} value={l.id}>{l.masked_source} · {l.entry_count || 0} ch</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button type="button" variant="outline" className="w-full" onClick={load} disabled={loading || !canLoad} data-testid="loader-load-button">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DownloadCloud className="h-4 w-4 mr-2" />} Load categories
      </Button>
    </div>
  );
}

function CategoryEditor({ categories, setCategories }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("live");
  const [dragIdx, setDragIdx] = useState(null);
  const [filter, setFilter] = useState("all");

  const add = () => {
    if (!name.trim()) return;
    setCategories([...categories, { id: `${type}::${name.trim()}`, name: name.trim(), source: name.trim(), type, enabled: true, order: categories.length }]);
    setName("");
  };
  const update = (i, patch) => setCategories(categories.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i) => setCategories(categories.filter((_, idx) => idx !== i).map((c, idx) => ({ ...c, order: idx })));
  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...categories];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setCategories(next.map((c, idx) => ({ ...c, order: idx })));
  };
  const move = (i, dir) => reorder(i, i + dir);

  const counts = { all: categories.length, live: 0, movie: 0, series: 0 };
  categories.forEach((c) => { counts[c.type] = (counts[c.type] || 0) + 1; });

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Add category (e.g. Sports)" data-testid="cat-name-input" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-36" data-testid="cat-type-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="live">Live TV</SelectItem>
            <SelectItem value="movie">Movies</SelectItem>
            <SelectItem value="series">Series</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" onClick={add} data-testid="cat-add-button"><Plus className="h-4 w-4" /></Button>
      </div>

      <div className="flex items-center gap-2 text-xs">
        {[["all", "All"], ["live", "Live"], ["movie", "Movies"], ["series", "Series"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`cat-filter-${k}`}
            className={`px-2.5 py-1 rounded-full border transition-colors duration-200 ${filter === k ? "bg-secondary text-foreground border-border" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label} <span className="opacity-60">{counts[k] || 0}</span>
          </button>
        ))}
        <span className="ml-auto text-muted-foreground">Drag to reorder</span>
      </div>

      <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
        {categories.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No categories yet. Import a link to auto-load them, or add manually.</p>}
        {categories.map((c, i) => {
          if (filter !== "all" && c.type !== filter) return null;
          const Icon = TYPE_ICON[c.type] || Tv;
          return (
            <div
              key={c.id || i}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { reorder(dragIdx, i); setDragIdx(null); }}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-2 rounded-md border bg-secondary/40 px-2.5 py-2 transition-colors duration-200 ${dragIdx === i ? "border-primary opacity-60" : "border-border"}`}
              data-testid={`cat-row-${i}`}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
              <div className="flex flex-col">
                <button type="button" onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === 0} data-testid={`cat-up-${i}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={i === categories.length - 1} data-testid={`cat-down-${i}`}><ChevronDown className="h-3.5 w-3.5" /></button>
              </div>
              <Icon className="h-4 w-4 text-accent shrink-0" />
              <Input
                value={c.name}
                onChange={(e) => update(i, { name: e.target.value })}
                className="flex-1 h-8 text-sm"
                data-testid={`cat-name-${i}`}
              />
              <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[c.type]}</Badge>
              <Switch checked={c.enabled} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`cat-toggle-${i}`} />
              <button type="button" onClick={() => remove(i)} className="text-destructive hover:opacity-70" data-testid={`cat-remove-${i}`}><X className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Templates() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [templates, setTemplates] = useState(null);
  const [panels, setPanels] = useState([]);
  const [poolLinks, setPoolLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", panel_id: "", description: "", categories: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/templates"); setTemplates(data); }
    catch { toast.error("Failed to load templates"); }
  };
  useEffect(() => {
    load();
    api.get("/panels").then(({ data }) => setPanels(data)).catch(() => {});
    api.get("/links").then(({ data }) => setPoolLinks(data)).catch(() => {});
  }, []);

  const panelName = (id) => panels.find((p) => p.id === id)?.name || "Any panel";

  const mergeLoaded = (loaded) => {
    setForm((f) => {
      const byId = new Set(f.categories.map((c) => c.id));
      const additions = loaded
        .filter((c) => !byId.has(c.id))
        .map((c, i) => ({ ...c, enabled: true, order: f.categories.length + i }));
      return { ...f, categories: [...f.categories, ...additions] };
    });
  };

  const openCreate = () => { setEditing(null); setForm({ name: "", panel_id: "", description: "", categories: [] }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, panel_id: t.panel_id, description: t.description || "", categories: t.categories.map((c) => ({ ...c })) }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/templates/${editing.id}`, { name: form.name, panel_id: form.panel_id || null, description: form.description, categories: form.categories });
        toast.success("Template saved");
      } else {
        await api.post("/templates", { name: form.name, panel_id: form.panel_id || null, description: form.description, categories: form.categories });
        toast.success("Template created — open it to load categories");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const copyTemplate = async (t) => {
    try { await api.post("/templates/copy", { master_id: t.id }); toast.success("Copied to your templates — edit freely"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const remove = async (t) => {
    try { await api.delete(`/templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const masters = (templates || []).filter((t) => t.is_master);
  const mine = (templates || []).filter((t) => !t.is_master && t.owner_id === user.id);

  const TemplateCard = ({ t }) => (
    <Card className="p-5 border-border bg-card transition-transform duration-200 hover:-translate-y-0.5" data-testid={`template-card-${t.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`grid place-items-center h-10 w-10 rounded-md ${t.is_master ? "bg-warning/10 text-warning" : "bg-primary/10 text-primary"}`}>
            {t.is_master ? <Crown className="h-5 w-5" /> : <User className="h-5 w-5" />}
          </div>
          <div>
            <p className="font-display font-semibold">{t.name}</p>
            <p className="text-xs text-muted-foreground">{panelName(t.panel_id)}</p>
          </div>
        </div>
        <Badge variant="outline" className={t.is_master ? "border-warning/40 text-warning" : "border-primary/40 text-primary"}>{t.is_master ? "Master" : "My Copy"}</Badge>
      </div>
      <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
        <span>{t.category_count} categories</span>
        <span className="text-success">{t.enabled_count} enabled</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3 min-h-6">
        {t.categories.slice(0, 5).map((c) => (
          <Badge key={c.id} variant="secondary" className={`text-[10px] ${!c.enabled ? "opacity-40 line-through" : ""}`}>{c.name}</Badge>
        ))}
        {t.category_count > 5 && <Badge variant="secondary" className="text-[10px]">+{t.category_count - 5}</Badge>}
      </div>
      <div className="flex items-center gap-1 mt-4 pt-4 border-t border-border">
        {t.is_master && !isAdmin && (
          <Button variant="ghost" size="sm" className="text-primary" onClick={() => copyTemplate(t)} data-testid={`template-copy-${t.id}`}><Copy className="h-4 w-4 mr-1.5" /> Copy to edit</Button>
        )}
        {(isAdmin || !t.is_master) && (
          <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
        )}
        {(isAdmin || (!t.is_master && t.owner_id === user.id)) && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-popover">
              <AlertDialogHeader><AlertDialogTitle>Delete {t.name}?</AlertDialogTitle>
                <AlertDialogDescription>Templates used by links cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => remove(t)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </Card>
  );

  return (
    <div data-testid="templates-page">
      <PageHeader overline="Content" title="Templates" description={isAdmin ? "Master channel packages. Resellers create editable copies." : "Copy a master template, then customize your own categories."}>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="add-template-button"><Plus className="h-4 w-4 mr-2" /> New Template</Button>
        )}
      </PageHeader>

      {!templates ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-lg" />)}
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">Master Templates</p>
            {masters.length === 0 ? (
              <Card className="p-8 text-center border-dashed"><LayoutTemplate className="h-10 w-10 mx-auto text-muted-foreground" /><p className="mt-3 text-sm text-muted-foreground">No master templates yet.</p></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{masters.map((t) => <TemplateCard key={t.id} t={t} />)}</div>
            )}
          </div>
          {!isAdmin && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">My Templates</p>
              {mine.length === 0 ? (
                <Card className="p-8 text-center border-dashed"><User className="h-10 w-10 mx-auto text-muted-foreground" /><p className="mt-3 text-sm text-muted-foreground">Copy a master template to start customizing.</p></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{mine.map((t) => <TemplateCard key={t.id} t={t} />)}</div>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${editing.name}` : "New Template"}</DialogTitle>
            <DialogDescription>Name your template, then load categories from a sample M3U/Xtream link or a Link Pool entry. Reorder, rename and enable/disable, then save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full Package" data-testid="template-name" /></div>
              <div className="space-y-2">
                <Label>Panel <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Select value={form.panel_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, panel_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="template-panel"><SelectValue placeholder="Any panel" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="none">Any panel</SelectItem>
                    {panels.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <CategoryLoader links={poolLinks} onLoaded={mergeLoaded} />
            <div className="space-y-2"><Label>Categories & channel order</Label>
              <CategoryEditor categories={form.categories} setCategories={(c) => setForm((f) => ({ ...f, categories: c }))} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !form.name} data-testid="template-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
